import { getMinMaxColumnKey } from '@/features/StructuredFilter/utils/rangeFilter.utils';
import { SOURCE_SEPERATOR } from '@/types/commonConst';
import { getFeedbackSourceName } from '@/types/feedbackConst';
import { SOURCE_COLUMNS } from '@/types/filterKeyConst';

type InitialFilterParseResult = {
  initialFormats: StructuredFilterQueryFormatT[];
  editableFilterKeys: string[];
};

type ParseStructuredFilter = (
  filter: StructuredFilter,
) => StructuredFilterQueryFormatT[];

const buildFallbackFormats = (filter: StructuredFilter): StructuredFilterQueryFormatT[] => {
  const formats: StructuredFilterQueryFormatT[] = [];
  Object.entries(filter).forEach(([columnKey, value]) => {
    if (!Array.isArray(value)) return;
    value.forEach((valueSet) => {
      if (columnKey === SOURCE_COLUMNS.SOURCE_TYPE) {
        const sourceValueSet = valueSet as StructuredFilterSourceObjectSet;
        const sourceValues = sourceValueSet.values
          .filter((v): v is SourceObject => typeof v === 'object' && !!v)
          .map((v) => {
            const sourceParts = [v.type, v.domain, v.channel].filter(
              (part) => part !== undefined && part !== null && String(part).length > 0,
            );
            return {
              filterKey: sourceParts.join(SOURCE_SEPERATOR),
              filterName: String(v.channel || v.domain || getFeedbackSourceName(v.type)),
            };
          });
        formats.push({
          columnKey,
          operator: sourceValueSet.op,
          filterValues: sourceValues,
        });
        return;
      }

      const stringNumberValueSet = valueSet as StructuredFilterStringNumberValueSet;
      const stringNumberValues = stringNumberValueSet.values.filter(
        (v): v is StringNumberValue => typeof v === 'string' || typeof v === 'number',
      );
      if (stringNumberValues.length === 0) return;
      formats.push({
        columnKey,
        operator: stringNumberValueSet.op,
        filterValues: stringNumberValues.map((v) => ({
          filterKey: v,
          filterName: String(v),
        })),
      });
    });
  });
  return formats;
};

const parseInitialFilterState = (
  filter: StructuredFilter,
  parseStructuredFilter?: ParseStructuredFilter,
): InitialFilterParseResult => {
  const initialFormats = parseStructuredFilter
    ? parseStructuredFilter(filter)
    : buildFallbackFormats(filter);
  const editableFilterKeys = new Set<string>();
  initialFormats.forEach((format) => {
    if (format.rangeValues !== undefined) {
      const { min, max } = getMinMaxColumnKey(format.columnKey);
      editableFilterKeys.add(min);
      editableFilterKeys.add(max);
      return;
    }
    editableFilterKeys.add(format.columnKey);
  });

  return {
    initialFormats,
    editableFilterKeys: [...editableFilterKeys],
  };
};

const buildFilterFormatSignature = (
  formats: StructuredFilterQueryFormatT[],
): string => {
  const normalizedFormats = formats
    .map((format) => ({
      columnKey: format.columnKey,
      operator: format.operator,
      filterKeys: (format.filterValues ?? [])
        .map((value) => String(value.filterKey))
        .sort((a, b) => a.localeCompare(b)),
      rangeMin: format.rangeValues?.min ?? null,
      rangeMax: format.rangeValues?.max ?? null,
    }))
    .sort((a, b) => {
      const aKey = `${a.columnKey}:${a.operator}`;
      const bKey = `${b.columnKey}:${b.operator}`;
      return aKey.localeCompare(bKey);
    });

  return JSON.stringify(normalizedFormats);
};

export { buildFilterFormatSignature,parseInitialFilterState };
export type { InitialFilterParseResult, ParseStructuredFilter };
